import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Loader2, ShieldCheck, CheckCircle2, RotateCcw, Calendar as CalendarIcon,
  Gauge, AlertCircle, Save,
} from "lucide-react";
import { toast } from "sonner";

/**
 * Admin-only panel — step progression controls:
 *   • Milestone toggle buttons (back-office controlled steps)
 *   • Timeline override form (expected date / duration / confidence %)
 *
 * Reads `matter.action_plan` and `matter.timeline_override` from the
 * GET /matters/:id response. After every action it bubbles `onChanged()`
 * so the parent reloads the matter to recompute everything.
 */
export default function StepProgressionPanel({ matterId, matter, accountability, onChanged }) {
  const milestoneSteps = (matter?.action_plan?.steps || []).filter(
    (s) => s.action_kind === "milestone",
  );
  const hasMilestones = milestoneSteps.length > 0;
  const override = matter?.timeline_override || null;

  return (
    <div className="border border-zinc-200 bg-white mb-6" data-testid="step-progression-panel">
      <div className="px-5 py-3 border-b border-zinc-200 bg-zinc-50 flex items-center gap-2">
        <ShieldCheck className="h-4 w-4 text-zinc-700" />
        <span className="text-overline text-zinc-700">Back-office step progression</span>
      </div>
      {hasMilestones && (
        <MilestoneList matterId={matterId} steps={milestoneSteps} onChanged={onChanged} />
      )}
      <TimelineOverride
        matterId={matterId}
        override={override}
        accountability={accountability}
        onChanged={onChanged}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------

function MilestoneList({ matterId, steps, onChanged }) {
  return (
    <div className="divide-y divide-zinc-100" data-testid="milestone-list">
      <div className="px-5 py-2 bg-white text-overline text-zinc-500">
        Milestones (firm-controlled)
      </div>
      {steps.map((step) => (
        <MilestoneRow
          key={step.id}
          matterId={matterId}
          step={step}
          onChanged={onChanged}
        />
      ))}
    </div>
  );
}

function MilestoneRow({ matterId, step, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState("");
  const [showNote, setShowNote] = useState(false);
  const isComplete = step.status === "complete";

  const toggle = async (value) => {
    setBusy(true);
    try {
      await api.post(`/admin/matters/${matterId}/milestones/${step.milestone_key}`, {
        value,
        note: value ? (note || null) : null,
      });
      toast.success(value ? `Marked "${step.title}" complete` : `Unmarked "${step.title}"`);
      setShowNote(false);
      setNote("");
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="px-5 py-3 flex items-start gap-4 flex-wrap" data-testid={`milestone-row-${step.milestone_key}`}>
      <div className="flex-1 min-w-[220px]">
        <div className="text-sm font-medium text-zinc-900">{step.title}</div>
        <div className="text-xs text-zinc-500 mt-0.5">{step.description}</div>
        {isComplete && step.completed_at && (
          <div className="text-xs text-emerald-700 mt-1">
            ✓ {new Date(step.completed_at).toLocaleDateString("en-ZA", { day: "2-digit", month: "short", year: "numeric" })}
            {step.completed_by ? ` · ${step.completed_by}` : ""}
            {step.note ? ` · "${step.note}"` : ""}
          </div>
        )}
        {!isComplete && step.status === "in_progress" && (
          <div className="text-xs text-amber-700 mt-1 flex items-center gap-1">
            <Loader2 className="h-3 w-3 animate-spin" /> Awaiting firm action
          </div>
        )}
      </div>
      <div className="flex items-center gap-2 shrink-0">
        {!isComplete && showNote && (
          <input
            type="text"
            placeholder="Note (optional)"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            className="text-xs px-2 py-1.5 border border-zinc-300 w-44"
            data-testid={`milestone-note-${step.milestone_key}`}
          />
        )}
        {!isComplete && !showNote && (
          <button
            onClick={() => setShowNote(true)}
            className="text-xs text-zinc-500 hover:text-zinc-900 underline"
          >
            Add note
          </button>
        )}
        {isComplete ? (
          <Button
            onClick={() => toggle(false)}
            disabled={busy}
            size="sm"
            variant="outline"
            className="rounded-none"
            data-testid={`milestone-unmark-${step.milestone_key}`}
          >
            <RotateCcw className="h-3 w-3 mr-1" /> Unmark
          </Button>
        ) : (
          <Button
            onClick={() => toggle(true)}
            disabled={busy}
            size="sm"
            className="rounded-none bg-emerald-700 hover:bg-emerald-800 text-white"
            data-testid={`milestone-mark-${step.milestone_key}`}
          >
            {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <CheckCircle2 className="h-3 w-3 mr-1" />}
            Mark {milestoneVerb(step.milestone_key)}
          </Button>
        )}
      </div>
    </div>
  );
}

function milestoneVerb(key) {
  const verbs = {
    lodged: "as lodged",
    registered: "as registered",
    notarised: "as notarised",
    registered_anc: "as registered",
    executed: "as executed",
    in_safekeeping: "in safekeeping",
    trust_registered: "as registered",
    letters_of_authority: "as issued",
    letters_of_executorship: "as issued",
    ld_approved: "as approved",
    distributed: "as distributed",
  };
  return verbs[key] || "complete";
}

// ---------------------------------------------------------------------------

function TimelineOverride({ matterId, override, accountability, onChanged }) {
  const [expectedDate, setExpectedDate] = useState(override?.expected_completion_at || "");
  const [duration, setDuration] = useState(override?.duration_days ?? "");
  const [confidence, setConfidence] = useState(override?.confidence_pct ?? "");
  const [reason, setReason] = useState(override?.reason || "");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setExpectedDate(override?.expected_completion_at || "");
    setDuration(override?.duration_days ?? "");
    setConfidence(override?.confidence_pct ?? "");
    setReason(override?.reason || "");
  }, [override]);

  const save = async () => {
    const body = {};
    if (expectedDate) body.expected_completion_at = expectedDate;
    if (duration !== "" && duration !== null) body.duration_days = Number(duration);
    if (confidence !== "" && confidence !== null) body.confidence_pct = Number(confidence);
    if (reason) body.reason = reason;
    if (Object.keys(body).length === 0) {
      toast.error("Set at least one of date, duration, or confidence.");
      return;
    }
    setBusy(true);
    try {
      await api.patch(`/admin/matters/${matterId}/timeline`, body);
      toast.success("Timeline override saved");
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const clear = async () => {
    setBusy(true);
    try {
      await api.delete(`/admin/matters/${matterId}/timeline`);
      toast.success("Timeline override cleared");
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const isOverridden = Boolean(override);
  const erd  = accountability?.expected_completion || {};
  const conf = accountability?.confidence || {};
  // The "system estimate" is what the engine would produce without an
  // override. When override is active, `erd.display` is already the
  // overridden value, so we have to reconstruct the underlying estimate
  // from `days_min/days_max` — but for clarity we just label the current
  // effective value as "Effective" and show the override marker.
  const effectiveExpected   = erd.display || "—";
  const effectiveConfidence = conf.score ?? "—";

  return (
    <div className="px-5 py-4 border-t border-zinc-100" data-testid="timeline-override">
      <div className="text-overline text-zinc-500 mb-3 flex items-center gap-2">
        <CalendarIcon className="h-3.5 w-3.5" /> Timeline control
        {isOverridden && (
          <span className="text-[10px] tracking-widest uppercase bg-amber-100 text-amber-800 px-1.5 py-0.5 ml-2"
                data-testid="timeline-override-active-pill">
            Override active
          </span>
        )}
      </div>

      {/* Computed vs. effective at-a-glance table */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4 text-sm">
        <div className="border border-zinc-200 px-3 py-2.5 bg-zinc-50" data-testid="timeline-system-card">
          <div className="text-[10px] tracking-widest uppercase text-zinc-500">System estimate</div>
          <div className="mt-0.5 font-medium text-zinc-900">
            {erd.days_min != null ? `${erd.days_min}–${erd.days_max} days` : effectiveExpected}
          </div>
          <div className="text-xs text-zinc-500">
            Pre-override · runs off task durations
          </div>
        </div>
        <div className={`border px-3 py-2.5 ${isOverridden ? "border-amber-300 bg-amber-50" : "border-zinc-200 bg-white"}`}
             data-testid="timeline-effective-card">
          <div className="text-[10px] tracking-widest uppercase text-zinc-500 flex items-center gap-2">
            Effective (client-facing)
            {isOverridden && <span className="bg-amber-200 text-amber-900 px-1.5 py-0.5">Overridden</span>}
          </div>
          <div className="mt-0.5 font-medium text-zinc-900">{effectiveExpected}</div>
          <div className="text-xs text-zinc-500">Confidence: <b className="text-zinc-700">{effectiveConfidence}%</b></div>
          {isOverridden && override?.updated_by && (
            <div className="text-[11px] text-amber-800 mt-1" data-testid="override-attribution">
              by <b>{override.updated_by}</b> on {new Date(override.updated_at).toLocaleString("en-ZA")}
              {override.reason ? ` — ${override.reason}` : ""}
            </div>
          )}
        </div>
      </div>

      <div className="text-overline text-zinc-500 mb-2">Set override</div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <label className="block">
          <span className="text-xs text-zinc-600">Override expected date</span>
          <input
            type="date"
            value={expectedDate}
            onChange={(e) => setExpectedDate(e.target.value)}
            className="mt-1 w-full text-sm px-2 py-1.5 border border-zinc-300"
            data-testid="override-expected-date"
          />
        </label>
        <label className="block">
          <span className="text-xs text-zinc-600">Or duration (+days from today)</span>
          <input
            type="number"
            min="0"
            value={duration}
            onChange={(e) => setDuration(e.target.value)}
            placeholder="e.g. 14"
            className="mt-1 w-full text-sm px-2 py-1.5 border border-zinc-300"
            data-testid="override-duration"
          />
        </label>
        <label className="block">
          <span className="text-xs text-zinc-600 flex items-center gap-1">
            <Gauge className="h-3 w-3" /> Confidence %
          </span>
          <input
            type="number"
            min="0"
            max="100"
            value={confidence}
            onChange={(e) => setConfidence(e.target.value)}
            placeholder="0–100"
            className="mt-1 w-full text-sm px-2 py-1.5 border border-zinc-300"
            data-testid="override-confidence"
          />
        </label>
        <label className="block md:col-span-1">
          <span className="text-xs text-zinc-600 flex items-center gap-1">
            <AlertCircle className="h-3 w-3" /> Reason (audit)
          </span>
          <input
            type="text"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. Bank delay"
            className="mt-1 w-full text-sm px-2 py-1.5 border border-zinc-300"
            data-testid="override-reason"
          />
        </label>
      </div>

      <div className="mt-3 flex gap-2 flex-wrap">
        <Button
          onClick={save}
          disabled={busy}
          size="sm"
          className="rounded-none bg-zinc-900 hover:bg-zinc-800 text-white"
          data-testid="override-save"
        >
          {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3 mr-1" />}
          Save override
        </Button>
        {override && (
          <Button
            onClick={clear}
            disabled={busy}
            size="sm"
            variant="outline"
            className="rounded-none"
            data-testid="override-clear"
          >
            Clear override
          </Button>
        )}
      </div>

      {override?.updated_by && (
        <p className="text-xs text-zinc-500 mt-2">
          Last changed by <b>{override.updated_by}</b> on {new Date(override.updated_at).toLocaleString("en-ZA")}
          {override.reason ? ` — ${override.reason}` : ""}
        </p>
      )}
    </div>
  );
}
